import React, { useEffect, useRef, useState } from 'react'
import BorderHeading from '../../components/heading/BorderHeading'
import ConfigurationHeader from '../../components/heading/ConfigurationHeader'
import { configurationAttributeType, defaultFooterConfiguationValues, footerOptions, languageOptions, newConfigurationNames } from '../../types'
import "./FooterConfiguration.css"
import { doFinalConfigSubmit, getMetaDataConfig, getNewConfiguration, overWriteConfig, postConfiguration, updateConfiguration } from '../../services/uiConfigService'
import store from '../../stateManagement/store'
import * as Sentry from "@sentry/react";
import { validateWithOtp } from '../../stateManagement/actions/validateOtpPopupActions'
import { openPopup } from '../../stateManagement/actions/popupActions'
import GenricMultiSelect from '../../components/Input/GenericMultiSelect'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCirclePlus, faGripLines } from '@fortawesome/free-solid-svg-icons'
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd'
import { manageUpdateAccessObj } from '../manageUpdateAccess/manageUpdateAccessTypes'
import { deepClone } from '@mui/x-data-grid/utils/utils'
import { Loader } from '../../components/loader/Loader'
import { Role } from '../../components/userModal/userModalType'
import { Autocomplete, TextField } from '@mui/material'
import ConfigComparePopup from '../../components/popup/ConfigComparePopup'
import ImportConfig from '../../components/importConfig/ImportConfig'

export const defaultFooterOptions: defaultFooterConfiguationValues  =  {
    bottomBarLayout: {
    "name": "bottomBarLayout",
    "type": "list",
    "value": [
        {
          "id": "home",
          "label": "HOME",
        },
        {
          "id": "order",
          "label": "MY ORDERS"
        },
        {
          "id": "cart",
          "label": "CART"
        }
    ],
    "docUrl": "",
    "description": "footer configuration",
    "possibleVals": [
        {
          "id": "home",
          "label": "HOME",
          "compulsory": true
        },
        {
          "id": "order",
          "label": "MY ORDERS",
          "compulsory": false
        },
        {
          "id": "cart",
          "label": "CART",
          "compulsory": false
        },
        {
          "id": "offer",
          "label": "OFFERS",
          "compulsory": false
        },
        {
          "id": "more",
          "label": "MORE",
          "compulsory": true
        },
        {
          "id": "ondc",
          "label": "ONDC",
          "compulsory": false
        }
      ]
  }
}

const footerOptionsMapping: { [key: string]: string} = {
    "HOME": "home",
    "MY ORDERS": "order",
    "CART": "cart",
    "OFFERS": "offer",
    "MORE": "more"
}

const FooterConfiguration = () => {
  const [retailerConfig, setRetailerConfig] = useState<configurationAttributeType<footerOptions[]>[]>([]);
  const [values, setValues] = useState<defaultFooterConfiguationValues>(deepClone(defaultFooterOptions));
  const [availableFooterOptions,setAvailableFooterOptions] = useState<string[]>([]);
  const [selectedFooterOptions,setSelectedFooterOptions] = useState<string[]>([]);  
  const [showLoader,setShowLoader] = useState<boolean>(false);
  const [role,setRole] = useState<string>("");
  const [appRoles,setAppRoles] = useState<string[]>(store.getState()?.manageRoleState?.allRoles ?? []);



  const maxValue = 5, minValue = 4;
  const getFooterConfiguration = async () => {
    try {
      setShowLoader(true);
      const resp = await getNewConfiguration(`${role}_app_layout_configuration` as newConfigurationNames);

      const retailer_app_layout_config: configurationAttributeType<footerOptions[]>[] = resp?.domainValues ?? [];

      const bottomBarLayout = retailer_app_layout_config.find(config => config.name === "bottomBarLayout");

      let finalValues = bottomBarLayout? [...bottomBarLayout.value] :  values.bottomBarLayout.value;
      
      finalValues = finalValues.map((option: footerOptions) => {
          const possibleValObj = values.bottomBarLayout.possibleVals.find((possibeValOption: any) => {
              return possibeValOption.id === option.id;
          });
          return {
              ...option,
              compulsory: possibleValObj?.compulsory || false
          }
      })
      //adding compulsory options
      for(let possibleVal of values.bottomBarLayout.possibleVals){
          const isPresent = finalValues.find((valueOption: footerOptions) => valueOption.id === possibleVal.id);
          if(possibleVal.compulsory && !isPresent){
              finalValues.push(possibleVal);
          }
      }
      values.bottomBarLayout.value = finalValues;

      const footerOptions = values.bottomBarLayout.possibleVals.filter((possibleValOption: footerOptions) => {
          return !values.bottomBarLayout.value.find((valueOption: footerOptions) => possibleValOption.id === valueOption.id);
      }).map((option: footerOptions) => option.label);

      setRetailerConfig(retailer_app_layout_config);
      setValues({...values});
      setAvailableFooterOptions(footerOptions);
      setShowLoader(false);
    } catch (err) {
      // Sentry.captureException(err);
    }
  }

  useEffect(() => {
      if(role) getFooterConfiguration();
  }, [role]);

  function handleSelectedFooterOptions(selectedOptions: string[]){
    setSelectedFooterOptions(selectedOptions);
  }
  function handleAddFooterOptions(){
        const newFooterValues = selectedFooterOptions.map((selectedOption: string) => {
            return { 
                label: selectedOption,
                id: footerOptionsMapping[selectedOption],
                compulsory: false  //as compulsory are added automatically on initial load(i.e only compulsory: false ones can be added through drop down)
            };
        })
        values.bottomBarLayout.value = [...values.bottomBarLayout.value, ...newFooterValues ];
        const newAvailableFooterOptions = availableFooterOptions.filter((availableOption: string) => {
            return !selectedFooterOptions.find((selectedOption: string) => {
                return selectedOption === availableOption;
            })
        })
        setValues({...values});
        setSelectedFooterOptions([]);
        setAvailableFooterOptions(newAvailableFooterOptions);
  }
  function handleRemove(targetLabel: string){
    const filteredValues = values.bottomBarLayout.value.filter((footerOption: footerOptions) => {
        return footerOption.label !== targetLabel;
    })
    values.bottomBarLayout.value = filteredValues;
    setValues({...values});
    setAvailableFooterOptions([targetLabel,...availableFooterOptions]);
  }
  function validateFooterConfig(){
    if(values.bottomBarLayout.value.length>maxValue){
        return `Footer Items can be ${maxValue} at max`;
    }else if(values.bottomBarLayout.value.length<minValue){
        return `Footer Items must be atleast ${maxValue} `;
    }
    return "";
  }
  const saveConfiguration = async (preview?: boolean) => {
    const message = validateFooterConfig();
    if(message!==""){
        openPopup("Alert",message);
        return;
    }

      try {
        let result = {...values};
        result.bottomBarLayout.value = result.bottomBarLayout.value.map((footerOption: footerOptions) => {
            const { id, label } = footerOption;
            return {
                id,
                label
            }
        });
        const configName:newConfigurationNames = `${role}_app_layout_configuration` as newConfigurationNames;
        const convertedVals = [...retailerConfig];
        Object.keys(result).forEach(key=>{
          const bottomBarObject = convertedVals.find(config=>config.name === key)
          if(bottomBarObject){
            bottomBarObject.value = result[key].value;
          }else{
            const configObj = {...result[key]};
            Reflect.deleteProperty(configObj, 'possibleVals');
            convertedVals.push(configObj);
          }
        })
        await doFinalConfigSubmit(configName,convertedVals);
      } catch (err) {
        // Sentry.captureException(err);
        console.log("err===", err)
        openPopup('Error',"Validation Error occured")
      }
  }
  function handleDragOnEnd(result: DropResult) {
    if(result.destination!==null && result.destination?.index!==0){
      const items = Array.from(values.bottomBarLayout.value);
      const [reorderedItem] = items.splice(result.source.index, 1);
      if (result.destination)
        items.splice(result.destination.index, 0, reorderedItem);

       values.bottomBarLayout.value = items; 
       setValues({...values});
    }
  }


  const importConfiguration = async (importedConfig:any)=>{
    const newConfigs = await overWriteConfig(importedConfig, {
      retailer_app_layout_configuration:['bottomBarLayout']
    })
    for(const configObject of newConfigs){
        await doFinalConfigSubmit(configObject.domainType, configObject.domainValues)
    }
    getFooterConfiguration();
  }
  const [importConfigState,setImportConfigState] = useState(false);


  return (
    <div className="footer_config_container">
      <ImportConfig importConfigState={importConfigState} setImportConfigState={setImportConfigState} configNames={[`${role}_app_layout_configuration`]} importConfigCallback={importConfiguration} />
      <ConfigurationHeader importConfiguration={()=>{setImportConfigState(true)}} heading={"Footer Configuration"} disable={!role || showLoader} saveConfiguration={() => saveConfiguration(false)} showConfirmPopup={true} />
      <div className="config_form_container">
        <BorderHeading>Role</BorderHeading>
        <div className='footer_config_body_container'>
          <Autocomplete 
              disablePortal
              id="appRoleSelection"
              options={appRoles}
              value={role}
              size="small"
              onChange={(event,value) => {
                if(value) setRole(value);
              }}
              renderInput={(params) => <TextField {...params} label="Select Role" />}
          />
        </div>
        {role && (!showLoader?(<><div className="form_header">
          <BorderHeading>Select the footer options</BorderHeading>
        </div>
        <div className='footer_config_body_container'>
            <div className='footer_config_multiselect_container'>
                <GenricMultiSelect
                    className="footer_config_multiselect"
                    options={availableFooterOptions}
                    label={"Select Footer Options"}
                    selectedOptions={selectedFooterOptions}
                    handleMultiSelectState={handleSelectedFooterOptions}
                    isRequired={false}
                    params={{ size: "small" }}
                />
                <FontAwesomeIcon
                    onClick={handleAddFooterOptions}
                    className="footer_config_plus_icon"
                    icon={faCirclePlus}
                />
            </div>
            <DragDropContext onDragEnd={handleDragOnEnd}>
            <Droppable droppableId="droppableFooterItems">
                {(provided) => (
                    <div className="footer_configuration_options_container" {...provided.droppableProps}
                    ref={provided.innerRef}>
                    {
                        values.bottomBarLayout.value.map((footerOption: footerOptions,index: number) => {
                            return (
                                <Draggable 
                                    key={footerOption.id}
                                    draggableId={footerOption.id}
                                    index={index}
                                    isDragDisabled={footerOption.id==="home"}
                                >
                                    {(provided) => (
                                        <div className='card' ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                            <div className={"item_card" + (footerOption.id==="home"?" footer_configuration_disabled_option":"")} >
                                                <span>
                                                <FontAwesomeIcon  className='transfer_list_grip_lines_icon' icon={faGripLines} />
                                                {footerOption.label}
                                                </span>
                                                {!footerOption.compulsory?<span className='homepage_icon_container'>
                                                <span className='circle_icon' onClick={() => handleRemove(footerOption.label)}><span className='hyphen_icon' /></span>
                                                </span>: <></>}
                                        </div>
                                        
                                    </div>
                                    )}
                                
                                </Draggable>
                            )
                        })
                    }
                    {provided.placeholder}
                    </div>
                )}
                </Droppable>
            </DragDropContext>
        </div></>):<Loader />)}
      </div>
    </div>
  )
}

export default FooterConfiguration